import { useMemo, useState } from 'react'
import CourseSearchPicker from '../../components/CourseSearchPicker.jsx'
import { lookupGhinHandicap } from '../../lib/ghinLookup.js'
import {
  calcCourseHandicapRaw, calcPlayingHandicap, strokesPerHoleForMode, resolveAllowance,
  ALLOWANCE_OPTIONS, DEFAULT_STROKE_INDEX,
} from './ggHandicap.js'

// G-Handicap — isolated sandbox handicap calculator. Self-contained (own
// engine ggHandicap.js, mock GHIN ggGhin.js, in-code courses ggCourses.js;
// zero live imports). AUDIT-CORRECT: playing handicap derives from the
// UNROUNDED course handicap; the rounded CH is shown for display only.
//
// Per-player allowance mirrors the live HandicapReviewTab: an all-players
// default plus a per-player pill override (gold = override, gray = inherited),
// a %-grid picker with a "Default (inherit)" tile, and Reset-all-to-default.

const GREEN = '#0A2818'
const GOLD = '#C9A84C'
const CREAM = 'var(--ge-cream-premium)'

const MODES = [
  { value: 'full', label: 'Full' },
  { value: 'cap1', label: 'Cap 1' },
  { value: 'lowest', label: 'Off Lowest' },
]

// allowance: null = inherit the all-players default; a number = per-player override.
const SEED = [
  { name: 'Mark Ratliff', hcp: '8.4', ghin: '1000001', ghinStatus: 'idle', allowance: null },
  { name: 'Cole Keese', hcp: '14.2', ghin: '1000002', ghinStatus: 'idle', allowance: null },
  { name: 'Ann Lee', hcp: '2.1', ghin: '1000003', ghinStatus: 'idle', allowance: null },
  { name: 'Ben Cole', hcp: '21.7', ghin: '1000004', ghinStatus: 'idle', allowance: null },
]

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '—'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const Seg = ({ options, value, onChange }) => (
  <div className="flex items-center gap-1 rounded-lg p-1" style={{ backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' }}>
    {options.map((o) => {
      const on = value === o.value
      return (
        <button key={String(o.value)} onClick={() => onChange(o.value)}
          className="rounded-md px-3 py-1 text-xs font-bold uppercase tracking-wider transition"
          style={on ? { backgroundColor: '#1B4D2E', color: '#fff' } : { backgroundColor: 'transparent', color: '#374151' }}>
          {o.label}
        </button>
      )
    })}
  </div>
)

// %-grid picker (port of the live PctGridPicker). allowDefault adds a tile that
// clears a per-player override (onPick(null) → inherit the event default).
function PctGridPicker({ open, title, subtitle, current, allowDefault, onPick, onClose }) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }} onClick={onClose}>
      <div className="rounded-2xl w-full max-w-sm p-5" style={{ backgroundColor: GREEN, border: `1px solid ${GOLD}55` }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-baseline justify-between mb-1">
          <h4 className="text-lg font-bold" style={{ color: GOLD }}>{title}</h4>
          <button onClick={onClose} className="text-sm px-1" style={{ color: `${CREAM}aa` }}>✕</button>
        </div>
        {subtitle ? <p className="text-xs mb-3" style={{ color: `${CREAM}99` }}>{subtitle}</p> : null}
        <div className="grid grid-cols-4 gap-2">
          {allowDefault ? (
            <button onClick={() => onPick(null)} className="col-span-4 rounded-xl py-2.5 text-sm font-bold uppercase tracking-wider"
              style={{ backgroundColor: `${CREAM}14`, color: CREAM, border: `1px solid ${CREAM}33` }}>
              Default (inherit event %)
            </button>
          ) : null}
          {ALLOWANCE_OPTIONS.map((p) => {
            const active = Number(current) === p
            return (
              <button key={p} onClick={() => onPick(p)} className="rounded-xl py-3 text-base font-extrabold transition"
                style={active ? { backgroundColor: GOLD, color: GREEN } : { backgroundColor: `${GOLD}1a`, color: GOLD, border: `1px solid ${GOLD}44` }}>
                {p}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default function GHandicap() {
  // Real course search — the SAME picker + golfCourseApi the tournament setup
  // uses. Defaults to Pine Bluff's Blue tee so the calculator works out of the box.
  const [courseSel, setCourseSel] = useState({
    courseName: 'Pine Bluff Country Club', coursePar: 71,
    slopeRating: 134, courseRating: 71.7, teeName: 'Blue',
    teeBoxes: [
      { name: 'Blue', slope: 134, rating: 71.7 },
      { name: 'White', slope: 130, rating: 70.4 },
      { name: 'Gold', slope: 137, rating: 73.4 },
      { name: 'Red', slope: 116, rating: 65.5 },
    ],
  })
  const [defaultAllowance, setDefaultAllowance] = useState(100)
  const [mode, setMode] = useState('full')
  const [rows, setRows] = useState(SEED)
  const [picker, setPicker] = useState(null) // null | {kind:'event'} | {kind:'player', i}

  const par = Number(courseSel?.coursePar) || 72
  const slope = Number(courseSel?.slopeRating) || 113
  const rating = Number(courseSel?.courseRating) || par

  const updateRow = (i, patch) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
  const resetAll = () => setRows((prev) => prev.map((r) => ({ ...r, allowance: null })))

  const runLookup = async (i) => {
    updateRow(i, { ghinStatus: 'loading' })
    const res = await lookupGhinHandicap(rows[i]?.ghin)
    if (res.ok) updateRow(i, { hcp: String(res.handicapIndex), name: res.fullName || rows[i]?.name, ghinStatus: 'ok' })
    else updateRow(i, { ghinStatus: 'notfound' })
  }

  const onPick = (val) => {
    const p = picker
    setPicker(null)
    if (!p) return
    if (p.kind === 'event') { if (val != null) setDefaultAllowance(Number(val)) }
    else if (p.kind === 'player') updateRow(p.i, { allowance: val == null ? null : Number(val) })
  }

  // Each row: raw CH → playing handicap using this player's effective allowance
  // (own override, else the all-players default) → per-hole strokes for the mode.
  const computed = useMemo(() => {
    const base = rows.map((r) => {
      const idx = parseFloat(r.hcp)
      const chRaw = Number.isFinite(idx)
        ? calcCourseHandicapRaw({ handicapIndex: idx, slopeRating: slope, courseRating: rating, par })
        : null
      const pct = resolveAllowance(r.allowance, defaultAllowance)
      const playing = chRaw == null ? null : calcPlayingHandicap(chRaw, pct)
      return { ...r, pct, chDisplay: chRaw == null ? null : Math.round(chRaw), playing }
    })
    const field = base.filter((p) => p.playing != null).map((p) => ({ playingHandicap: p.playing }))
    return base.map((p) => {
      if (p.playing == null) return { ...p, strokesTotal: null }
      const strokes = strokesPerHoleForMode(mode, { playingHandicap: p.playing }, field, DEFAULT_STROKE_INDEX)
      return { ...p, strokesTotal: strokes.reduce((a, b) => a + b, 0) }
    })
  }, [rows, slope, rating, par, defaultAllowance, mode])

  const ghinBadge = (s) => s === 'ok' ? '✓' : s === 'notfound' ? '✕' : s === 'loading' ? '…' : '↧'

  return (
    <div className="card">
      <div className="space-y-3 mb-3">
        <div>
          <label className="block text-base font-bold text-gray-800 mb-1">Course — search any course</label>
          <CourseSearchPicker value={courseSel} onChange={setCourseSel} />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-base font-bold text-gray-800">Allocation</span>
          <Seg options={MODES} value={mode} onChange={setMode} />
        </div>

        {/* All-players default allowance — tap to change (opens the % grid). */}
        <div className="flex items-center gap-3">
          <button onClick={() => setPicker({ kind: 'event' })}
            className="rounded-xl px-4 py-2 flex items-center gap-2 text-left transition hover:brightness-110"
            style={{ backgroundColor: GREEN, border: `1.5px solid ${GOLD}` }}>
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: CREAM }}>All-players default</span>
            <span className="text-2xl font-extrabold leading-none" style={{ color: GOLD }}>{defaultAllowance}<span className="text-base">%</span></span>
          </button>
          <button className="btn-ghost text-xs" onClick={resetAll}>↺ Reset all to default</button>
        </div>

        <p className="text-base text-gray-600">
          Playing handicap uses the <b>unrounded</b> course handicap × each player's allowance (own override, else the default), audit-corrected.
          CH shown is rounded for display only.
        </p>
      </div>

      <table className="w-full text-lg" style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f6f6f2', color: '#555' }}>
            {['Player', 'Index', '(GH)#', 'CH', 'Allowance', 'PH', `Strokes (${mode})`].map((h) => (
              <th key={h} style={{ border: '1px solid #ddd', padding: '11px 14px', textAlign: 'left', fontSize: 18, fontWeight: 800 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {computed.map((p, i) => {
            const override = p.allowance != null
            return (
              <tr key={i}>
                <td style={cell}><input className="border rounded px-2 py-1.5 text-base w-44" value={p.name} onChange={(e) => updateRow(i, { name: e.target.value })} /></td>
                <td style={cell}><input className="border rounded px-2 py-1.5 text-base w-20" value={p.hcp} onChange={(e) => updateRow(i, { hcp: e.target.value, ghinStatus: 'idle' })} /></td>
                <td style={cell}>
                  <div className="flex items-center gap-1.5">
                    <input className="border rounded px-2 py-1.5 text-base w-32" value={p.ghin} onChange={(e) => updateRow(i, { ghin: e.target.value, ghinStatus: 'idle' })} />
                    <button className="btn-ghost text-lg" title="(GH)# lookup" onClick={() => runLookup(i)}
                      style={{ color: p.ghinStatus === 'ok' ? '#1B7A3E' : p.ghinStatus === 'notfound' ? '#B42318' : '#374151' }}>
                      {ghinBadge(p.ghinStatus)}
                    </button>
                  </div>
                </td>
                <td style={cell}>{p.chDisplay ?? '—'}</td>
                <td style={cell}>
                  <button onClick={() => setPicker({ kind: 'player', i })}
                    className="inline-flex items-center gap-1.5 rounded-full pl-2.5 pr-3 py-1 text-xs font-bold transition"
                    style={override ? { backgroundColor: GOLD, color: GREEN } : { backgroundColor: '#F3F4F6', color: '#6B7280', border: '1px solid #E5E7EB' }}
                    title={override ? 'Per-player override — tap to change' : 'Inherited default — tap to override'}>
                    <span className="opacity-70">{initials(p.name)}</span>
                    <span>{p.pct}%</span>
                  </button>
                </td>
                <td style={cell}><b>{p.playing ?? '—'}</b></td>
                <td style={cell}>{p.strokesTotal ?? '—'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <PctGridPicker
        open={!!picker}
        title={picker?.kind === 'player' ? `${rows[picker.i]?.name || 'Player'} — allowance` : 'Default allowance'}
        subtitle={picker?.kind === 'player' ? 'Overrides the all-players default for this player.' : 'Applies to every player without a per-player override.'}
        current={picker?.kind === 'player' ? (rows[picker.i]?.allowance ?? defaultAllowance) : defaultAllowance}
        allowDefault={picker?.kind === 'player'}
        onPick={onPick}
        onClose={() => setPicker(null)}
      />
    </div>
  )
}

const cell = { border: '1px solid #ddd', padding: '11px 14px', fontSize: 18 }
